import type { AssetSheetTabProblem } from '@atlas/types'
import { Check, Loader, X } from 'lucide-react'
import { Link } from 'react-router-dom'
import { type LastSync, useLastSync } from '@/hooks/useAssetSheet'
import { formatShortDateTime } from '@/lib/format-short-date-time'
import { AssetSheetParam, AssetSheetView, describeSyncCounts, viewSearch } from './asset-sheet-view'

export interface LastSyncSummaryProps {
	running: boolean
	/** When the last sync read a row, for when its outcome is no longer among the recent activity. */
	lastSyncedAt: string | null
}

/** The last sync's outcome under the page header, each tab it skipped with a link to that tab's column map. */
export function LastSyncSummary({ running, lastSyncedAt }: LastSyncSummaryProps) {
	const last = useLastSync()
	return <LastSyncLines running={running} lastSyncedAt={lastSyncedAt} last={last} />
}

/** The summary from props, for the story. */
export function LastSyncLines({ running, lastSyncedAt, last }: LastSyncSummaryProps & { last?: LastSync | null }) {
	if (running)
		return (
			<p className="flex items-center gap-ds-2 text-ds-sm text-ds-text">
				<Loader aria-hidden className="h-4 w-4 text-ds-warning" />
				Sync running
			</p>
		)
	if (!last)
		return lastSyncedAt ? (
			<p className="text-ds-sm text-ds-text-muted">Last synced {formatShortDateTime(lastSyncedAt)}</p>
		) : null
	return (
		<div className="flex flex-col gap-ds-1 text-ds-sm">
			<p className="flex items-start gap-ds-2 text-ds-text">
				{last.failure ? (
					<X aria-hidden className="mt-0.5 h-4 w-4 shrink-0 text-ds-danger" />
				) : (
					<Check aria-hidden className="mt-0.5 h-4 w-4 shrink-0 text-ds-success" />
				)}
				<span>
					<span className="font-medium">
						{last.failure ? 'Last sync failed' : 'Last sync'} {formatShortDateTime(last.at)}
					</span>
					{last.failure ? `: ${last.failure}` : last.counts && ` · ${describeSyncCounts(last.counts)}`}
				</span>
			</p>
			{tabsByReason(last.tabProblems).map(([reason, tabs]) => (
				<SkippedTabs key={reason} reason={reason} tabs={tabs} />
			))}
		</div>
	)
}

/** The skipped tabs grouped by reason, so a header missing from every tab reads once. */
function tabsByReason(problems: AssetSheetTabProblem[]): [string, string[]][] {
	const groups = new Map<string, string[]>()
	for (const { tab, reason } of problems) groups.set(reason, [...(groups.get(reason) ?? []), tab])
	return [...groups]
}

/** Tabs a sync skipped for one reason, each with a link to its column map. */
function SkippedTabs({ reason, tabs }: { reason: string; tabs: string[] }) {
	const many = tabs.length > 1
	return (
		<p className="flex flex-wrap gap-x-ds-2 pl-ds-6 text-ds-danger">
			<span>
				{many ? 'Tabs' : 'Tab'} {tabs.join(', ')} skipped: {reason}. {many ? 'Their' : 'Its'} rows keep what they had.
			</span>
			<span className="flex flex-wrap gap-x-ds-2 text-ds-text">
				{many && 'Check the column map of'}
				{tabs.map((tab) => (
					<Link
						key={tab}
						to={{ search: viewSearch(AssetSheetView.COLUMN_MAP, { [AssetSheetParam.SHEET_TAB]: tab }) }}
						aria-label={many ? `Check the ${tab} column map` : undefined}
						className="underline underline-offset-2 hover:text-ds-text-muted"
					>
						{many ? tab : `Check the ${tab} column map`}
					</Link>
				))}
			</span>
		</p>
	)
}
